import * as vp from 'vopay';
import { Box, Button, Table, Icon, Checkbox, Tooltip, Pagination } from 'vopay';
import { FunctionComponent, useState, useEffect, JSX } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import styles from './AccountingSystemOpen.module.scss';
import { createPortal } from 'react-dom';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import BankingUpdateDialogue from './BankingUpdateDialogue/BankingUpdateDialogue';
import BulkPaymentDialogue from './BulkPaymentDialogue/BulkPaymentDialogue';
import AccountingSystemDialogue from './AccountingSystemDialogue/AccountingSystemDialogue';
import AccountingSystemSingleDialogue from './AccountingSystemSingleDialogue/AccountingSystemSingleDialogue';
import AccountingSystemScheduleDialogue from './AccountingSystemScheduleDialogue/AccountingSystemScheduleDialogue';
import PartiallyPaidDialogue from './PartiallyPaidDialogue/PartiallyPaidDialogue';
import AccountingSystemContext, { AccountingSystemCtx } from './AccountingSystemDialogue/AccountingSystemContext';
import { AccountingSystemDialogueHandler, DialogueHandlerProps } from './AccountingSystemDialogue/types';
import usePostInvoices from '@/hooks/useApi/usePostInvoices';
import usePostBills from '@/hooks/useApi/usePostBills';
import usePostCancelBill from '@/hooks/useApi/usePostCancelBill';
import usePostCancelInvoice from '@/hooks/useApi/usePostCancelInvoice';
import useFetchFinancialInstitutions from '@/hooks/useApi/useFetchFinancialInstitutions';
import { getAvailablePaymentMethodsDropdown } from './utils';
import { Payment } from './types';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/providers/Toast.provider';
import { useDataFetchIndicator } from '@/hooks';
import TransactionStatus from '@/components/UI/TransactionStatus/TransactionStatus';

type AccountingSystemOpenProps = {
  BillsOrInvoices: vp.BillOrInvoiceRailz[];

  OnSuccess: () => void;
  ServiceName: string;
  BusinessName: string;
  AccountBalance: vp.AccountBalanceResponse;
  Type: 'Payable' | 'Receivable';
  onActionsReady?: (actions: { onScheduleAll: () => void }) => void;
};
enum ToastVisibilityState {
  ReceivableWithPaymentMethods = 'ReceivableWithPaymentMethods',
  ScheduledForCollection = 'ScheduledForCollection',
  PaymentMethodSaved = 'PaymentMethodSaved',
  CollectionRequestSent = 'CollectionRequestSent',
  CollectionScheduled = 'CollectionScheduled',
  MultipleRecipientsRequestSent = 'MultipleRecipientsRequestSent',
  MultipleRecipientsScheduled = 'MultipleRecipientsScheduled',
  PaymentMethodSavedToRecipients = 'PaymentMethodSavedToRecipients',
  ScheduledForDate = 'ScheduledForDate',
  PartialCollection = 'PartialCollection',
  FinalCollectionRequestSent = 'FinalCollectionRequestSent',
  ScheduleDateUpdate = 'ScheduleDateUpdate',
}

type FC = FunctionComponent<AccountingSystemOpenProps>;
const AccountingSystemOpen: FC = ({
  BillsOrInvoices,
  Type,
  BusinessName,
  ServiceName,
  AccountBalance,
  OnSuccess,
  onActionsReady,
}: AccountingSystemOpenProps) => {
  const history = useHistory();
  const { response } = useClientLogin();
  const [financialInstitutionsResp, fetchFinancialInstitutions] = useFetchFinancialInstitutions({});
  const [allCheckbox, setAllCheckbox] = useState(false);
  const [tempBillsOrInvoices, setTempBillsOrInvoices] = useState<vp.TemporaryBillsOrInvoicesRails[]>([]);
  const [selectedBillsOrInvoices, setSelectedBillsOrInvoices] = useState<vp.TemporaryBillsOrInvoicesRails[]>([]);
  const [confirmedBillsOrInvoices, setConfirmedBillsOrInvoices] = useState<vp.TemporaryBillsOrInvoicesRails[]>([]);
  const [confirmedDate, setConfirmedDate] = useState('');
  const [isReceivableWithPaymentMethodsVisible, setIsReceivableWithPaymentMethodsVisible] = useState(false);
  const [isScheduledForCollectionVisible, setIsScheduledForCollectionVisible] = useState(false);
  const [isPaymentMethodSavedVisible, setIsPaymentMethodSavedVisible] = useState(false);
  const [isCollectionRequestSentVisible, setIsCollectionRequestSentVisible] = useState(false);
  const [isCollectionScheduledVisible, setIsCollectionScheduledVisible] = useState(false);
  const [isMultipleRecipientsRequestSentVisible, setIsMultipleRecipientsRequestSentVisible] = useState(false);
  const [isMultipleRecipientsScheduledVisible, setIsMultipleRecipientsScheduledVisible] = useState(false);
  const [isPaymentMethodSavedToRecipientsVisible, setIsPaymentMethodSavedToRecipientsVisible] = useState(false);

  const [isPartialCollectionVisible, setIsPartialCollectionVisible] = useState(false);
  const [isFinalCollectionRequestSentVisible, setIsFinalCollectionRequestSentVisible] = useState(false);
  const [showSuccessScheduleDateUpdate, setShowSuccessScheduleDateUpdated] = useState(false);
  const [showSuccessScheduleCanceled, setShowSuccessScheduleCanceled] = useState(false);

  const [showInsufficientFunds, setShowInsufficientFunds] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [clientName, setClientName] = useState('');
  const [showBulkPayment, setShowBulkPayment] = useState(false);

  const [isSingleTransaction, setIsSingleTransaction] = useState(false);

  const [updatingItem, setUpdatingItem] = useState<vp.TemporaryBillsOrInvoicesRails>();
  const [, dataFetchIndicator] = useDataFetchIndicator();
  const { loading: postInvoicesLoading, response: collectInvoicesResponse, postInvoices } = usePostInvoices();
  const { loading: postBillsLoading, response: payBillsResponse, postBills } = usePostBills();
  const [showSingleCollect, setShowSingleCollect] = useState(false);
  const [showNoPaymentMethod, setShowNoPaymentMethod] = useState(false);
  const [showCancelSchedule, setShowCancelSchedule] = useState(false);
  const [showEditSchedule, setShowEditSchedule] = useState(false);

  useEffect(() => {
    dataFetchIndicator(postInvoicesLoading);
  }, [postInvoicesLoading]);

  useEffect(() => {
    dataFetchIndicator(postBillsLoading);
  }, [postBillsLoading]);

  const [isUpdateInfo, setUpdateInfo] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(String(Date.now()));

  const [isEditTransaction, setIsEditTransaction] = useState(false);
  const [cancelBillResponse, cancelBill, , resetCancelBill] = usePostCancelBill({});
  const [cancelInvoiceResponse, cancelInvoice, , resetInvoice] = usePostCancelInvoice({});
  const [showScheduleAll, setShowScheduleAll] = useState(false);
  const [hasItemsWithNoPaymentMethod, setHasItemsWithNoPaymentMethod] = useState(false);
  const [toastState, setToastState] = useState<ToastVisibilityState>(ToastVisibilityState.PartialCollection);
  const [showScheduleCustomDate, setShowScheduleCustomDate] = useState(false);
  const [showPartiallyPaidDialogue, setShowPartiallyPaidDialogue] = useState(false);
  const [partiallyPaidPayments, setPartiallyPaidPayments] = useState<Payment[]>([]);
  const { t } = useTranslation('translations', { keyPrefix: 'AccountingSystem.OpenTab' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const toast = useToast();

  const [currentPage, setCurrentPage] = useState(0);
  const PER_PAGE = 20;
  const offset = currentPage * PER_PAGE;
  const pageCount = Math.ceil(tempBillsOrInvoices.length / PER_PAGE);

  useEffect(() => {
    if (cancelBillResponse && cancelBillResponse.Success) {
      setShowSuccessScheduleCanceled(true);
      resetCancelBill();
      OnSuccess();
    }
  }, [cancelBillResponse]);
  useEffect(() => {
    if (cancelInvoiceResponse && cancelInvoiceResponse.Success) {
      setShowSuccessScheduleCanceled(true);
      resetInvoice();
      OnSuccess();
    }
  }, [cancelInvoiceResponse]);
  useEffect(() => {
    if (showSuccessScheduleCanceled) {
      setShowSuccessScheduleCanceled(false);
    }
  }, [showSuccessScheduleCanceled]);
  useEffect(() => {
    if (showSuccessScheduleDateUpdate) {
      setShowSuccessScheduleDateUpdated(false);
    }
  }, [showSuccessScheduleDateUpdate]);

  useEffect(() => {
    if (isReceivableWithPaymentMethodsVisible) {
      setIsReceivableWithPaymentMethodsVisible(false);
    }
  }, [isReceivableWithPaymentMethodsVisible]);

  useEffect(() => {
    if (isScheduledForCollectionVisible) {
      setIsScheduledForCollectionVisible(false);
    }
  }, [isScheduledForCollectionVisible]);

  useEffect(() => {
    if (isPaymentMethodSavedVisible) {
      setIsPaymentMethodSavedVisible(false);
    }
  }, [isPaymentMethodSavedVisible]);

  useEffect(() => {
    if (isCollectionRequestSentVisible) {
      setIsCollectionRequestSentVisible(false);
    }
  }, [isCollectionRequestSentVisible]);

  useEffect(() => {
    if (isCollectionScheduledVisible) {
      setIsCollectionScheduledVisible(false);
    }
  }, [isCollectionScheduledVisible]);

  useEffect(() => {
    if (isMultipleRecipientsRequestSentVisible) {
      setIsMultipleRecipientsRequestSentVisible(false);
    }
  }, [isMultipleRecipientsRequestSentVisible]);

  useEffect(() => {
    if (isMultipleRecipientsScheduledVisible) {
      setIsMultipleRecipientsScheduledVisible(false);
    }
  }, [isMultipleRecipientsScheduledVisible]);

  useEffect(() => {
    if (isPaymentMethodSavedToRecipientsVisible) {
      setIsPaymentMethodSavedToRecipientsVisible(false);
    }
  }, [isPaymentMethodSavedToRecipientsVisible]);

  useEffect(() => {
    if (isPartialCollectionVisible) {
      setIsPartialCollectionVisible(false);
    }
  }, [isPartialCollectionVisible]);

  useEffect(() => {
    if (isFinalCollectionRequestSentVisible) {
      setIsFinalCollectionRequestSentVisible(false);
    }
  }, [isFinalCollectionRequestSentVisible]);
  const onPayOrCollect = async (selectedList: vp.TemporaryBillsOrInvoicesRails[], state: ToastVisibilityState) => {
    setToastState(state);
    if (Type === 'Receivable') {
      try {
        await postInvoices({
          BusinessName,
          ServiceName,
          Invoices: selectedList,
        });
      } catch (error) {
        const msg = error instanceof Error ? error.message : tGlobal('errorOccurred');
        setShowError(true);

        setErrorMessage(msg);
      }
    } else if (checkBalance(selectedList)) {
      try {
        await postBills({
          BusinessName,
          ServiceName,
          Bills: selectedList,
        });
      } catch (error) {
        const msg = error instanceof Error ? error.message : tGlobal('errorOccurred');
        setShowError(true);

        setErrorMessage(msg);
      }
    }
  };
  const collectOrPayAllOnDueDate = () => {
    const processedList = formatDateOfObjects(tempBillsOrInvoices);
    onPayOrCollect(processedList, ToastVisibilityState.ScheduledForCollection);
  };

  const collectOrPayBulkSchedule = (selectedList: vp.TemporaryBillsOrInvoicesRails[], state: ToastVisibilityState) => {
    const processedList = formatDateOfObjects(selectedList);
    onPayOrCollect(processedList, state);
  };

  const updateScheduleDate = (
    newDate: string,
    selectedList: vp.TemporaryBillsOrInvoicesRails[],
    state: ToastVisibilityState,
  ) => {
    const processedList = selectedList.map((billOrInvoice) => ({
      ...billOrInvoice,
      scheduledDate: newDate,
    }));

    onPayOrCollect(processedList, state);
  };

  const accountingSystemCtx: AccountingSystemCtx = {
    showInsufficientFunds,
    showError,
    errorMessage,
    clientName,
    showCancel,
    type: Type,
    showNoPaymentMethod,
    showCancelSchedule,
    hasItemsWithNoPaymentMethod,
    showScheduleAll,
    showScheduleCustomDate,
  };
  const handlers = {
    handleAccountDialogue: (handlerProps: DialogueHandlerProps<AccountingSystemDialogueHandler>) => {
      const { type: typeHandler, state: state2, action = 'default' } = handlerProps;

      const dialogueHandlers: AccountingSystemDialogueHandler = {
        scheduleCustomDate: {
          confirm: {
            default: () => {
              updateScheduleDate(confirmedDate, confirmedBillsOrInvoices, ToastVisibilityState.ScheduleDateUpdate);
              setShowScheduleCustomDate(false);
            },
          },
          fail: {
            default: () => {
              setShowScheduleCustomDate(false);
              setShowBulkPayment(true);
            },
          },
        },
        cancelSchedule: {
          confirm: {
            default: () => {
              setShowCancelSchedule(false);
              if (Type === 'Receivable') {
                cancelInvoice({
                  InvoiceRefID: updatingItem?.id || '',
                });
              } else {
                cancelBill({
                  BillRefID: updatingItem?.id || '',
                });
              }
            },
          },
          fail: {
            default: () => {
              setShowCancelSchedule(false);
            },
          },
        },
        noPaymentMethod: {
          confirm: {
            default: () => {
              setShowNoPaymentMethod(false);
            },
          },
          close: {
            default: () => {
              setShowNoPaymentMethod(false);
            },
          },
        },
        error: {
          confirm: {
            default: () => {
              setShowError(false);
            },
          },
        },
        cancel: {
          confirm: {
            default: () => {
              setShowCancel(false);
            },
          },
          fail: {
            default: () => {
              setShowCancel(false);
              setShowBulkPayment(true);
            },
          },
        },
        insufficient: {
          confirm: {
            default: () => {
              history.push('/fund-my-account');
            },
          },
          fail: {
            default: () => {
              setShowInsufficientFunds(false);
            },
          },
        },
        scheduleAll: {
          confirm: {
            default: () => {
              collectOrPayAllOnDueDate();
            },
          },
          fail: {
            default: () => {
              setShowScheduleAll(false);
            },
          },
        },

        singleCollect: {
          confirm: {
            default: () => {},
          },
          fail: {
            default: () => {},
          },
        },
      };
      const dialogueState = dialogueHandlers[typeHandler][state2];
      const handler = dialogueState ? dialogueState[action] || dialogueState.default : () => {};

      handler();
    },
  };
  useEffect(() => {
    fetchFinancialInstitutions({
      Country: response.Country,
    });
  }, []);

  // TODO: Refactor this logic to await functions be carful of the isSingleTransaction, isEditTransaction isn't cached on function creation
  useEffect(() => {
    if (collectInvoicesResponse) {
      if (collectInvoicesResponse.Success) {
        if (isEditTransaction) {
          setShowSuccessScheduleDateUpdated(true);
          setIsEditTransaction(false);
        } else if (isSingleTransaction) {
          setIsSingleTransaction(false);
        }
        OnSuccess();
        enableToastVisibility(toastState);
      }
    }
  }, [collectInvoicesResponse]);
  useEffect(() => {
    if (payBillsResponse) {
      if (payBillsResponse.Success) {
        if (isEditTransaction) {
          setShowSuccessScheduleDateUpdated(true);
          setIsEditTransaction(false);
        } else if (isSingleTransaction) {
          setIsSingleTransaction(false);
        }
        OnSuccess();
        enableToastVisibility(toastState);
      }
    }
  }, [payBillsResponse]);

  useEffect(() => {
    setTempBillsOrInvoices(
      BillsOrInvoices.map((billOrInvoice) => {
        const hasDetails = {
          hasBankAccount: !!(
            billOrInvoice.Customer &&
            billOrInvoice.Customer.BankAccount &&
            billOrInvoice.Customer.BankAccount.FinancialInstitutionNumber &&
            billOrInvoice.Customer.BankAccount.BranchTransitNumber &&
            billOrInvoice.Customer.BankAccount.AccountNumber &&
            ((response.Country === 'CA' &&
              ((Type === 'Receivable' && vp.toBool(response.EFTCollectEnabled)) ||
                (Type === 'Payable' &&
                  (vp.toBool(response.EFTSendEnabled) || vp.toBool(response.RTRWithdrawEnabled))))) ||
              (response.Country === 'US' &&
                ((Type === 'Receivable' && vp.toBool(response.ACHFundEnabled)) ||
                  (Type === 'Payable' &&
                    (vp.toBool(response.ACHWithdrawEnabled) || vp.toBool(response.RTPWithdrawEnabled))))))
          ),
          hasInterac: !!(
            (
              billOrInvoice.Customer &&
              billOrInvoice.Customer.Interac &&
              (billOrInvoice.Customer.Interac.RecipientEmailAddress ||
                billOrInvoice.Customer.Interac?.RecipientPhoneNumber) &&
              (billOrInvoice.Customer.Interac.Question || Type === 'Receivable') &&
              (billOrInvoice.Customer.Interac.Answer || Type === 'Receivable') &&
              ((response.Country === 'CA' && Type === 'Receivable' && vp.toBool(response.InteracMoneyRequestEnabled)) ||
                (response.Country === 'CA' && Type === 'Payable' && vp.toBool(response.InteracBulkPayoutEnabled)))
            )
            // country canada money request Receivable, payout, payable
          ),
          hasDebitCard: !!(
            billOrInvoice.Customer &&
            billOrInvoice.Customer.DebitCard &&
            billOrInvoice.Customer.DebitCard.PushToCardToken &&
            vp.toBool(response.VisaDirectEnabled) &&
            Type === 'Payable'
          ),
          hasCreditCard: !!(
            billOrInvoice.Customer &&
            billOrInvoice.Customer.CreditCard &&
            billOrInvoice.Customer.CreditCard.CreditCardToken &&
            vp.toBool(response.CreditCardEnabled) &&
            Type === 'Receivable'
          ),
        };
        return {
          ...billOrInvoice,
          scheduledDate: billOrInvoice.ScheduledDate,
          Customer: {
            ...(billOrInvoice.Customer && {
              ...billOrInvoice.Customer,
              BankAccount: {
                RoutingNumber: billOrInvoice.Customer.BankAccount?.ABARoutingNumber,
                AccountNumber: billOrInvoice.Customer.BankAccount?.AccountNumber || '',
                BranchTransitNumber: billOrInvoice.Customer.BankAccount?.BranchTransitNumber,
                FinancialInstitutionNumber: billOrInvoice.Customer.BankAccount?.FinancialInstitutionNumber,
                PhoneNumber: billOrInvoice.Customer.BankAccount?.Phone || '',
                City: billOrInvoice.Customer.BankAccount?.City || '',
                Address: billOrInvoice.Customer.BankAccount?.Address || '',
                State: billOrInvoice.Customer.BankAccount?.State || '',
                ZipCode: billOrInvoice.Customer.BankAccount?.PostalCode || '',
                ACTCurrencies_ID: response.Country === 'CA' ? 2 : 1,
              },
              Interac: {
                EmailAddress: billOrInvoice.Customer.Interac?.RecipientEmailAddress,
                PhoneNumber: billOrInvoice.Customer.Interac?.RecipientPhoneNumber,
                Question: billOrInvoice.Customer.Interac?.Question,
                Answer: billOrInvoice.Customer.Interac?.Answer,
                Language: billOrInvoice.Customer.Interac?.Language || 'en',
              },
              CreditCard: {
                ...billOrInvoice.Customer.CreditCard,
                Token: billOrInvoice.Customer.CreditCard?.CreditCardToken,
              },
              DebitCard: {
                ...billOrInvoice.Customer.DebitCard,
                Token: billOrInvoice.Customer.DebitCard?.PushToCardToken,
              },
            }),
          },
          PaymentRail: vp.getPaymentRailsAccountingSoftware(
            Object.values(vp.getAvailablePaymentMethodsDropdown(hasDetails, response.Country, Type))[0] || '',
            response.Country,
            Type,
          ),
          ...hasDetails,
          isSelected: allCheckbox,
          isExpanded: false,
          PaidAmount: billOrInvoice.amountDue ? billOrInvoice.amountDue : billOrInvoice.totalAmount,
          paymentMethodUser:
            Object.values(vp.getAvailablePaymentMethodsDropdown(hasDetails, response.Country, Type))[0] || '',
        };
      }),
    );
  }, [BillsOrInvoices]);
  useEffect(() => {
    setSelectedBillsOrInvoices(tempBillsOrInvoices.filter((billOrInvoice) => billOrInvoice.isSelected));
  }, [tempBillsOrInvoices]);

  const onCheckedInvoice = (id: string) => {
    setTempBillsOrInvoices(
      tempBillsOrInvoices.map((billOrInvoice) => {
        if (billOrInvoice.id === id) {
          return { ...billOrInvoice, isSelected: !billOrInvoice.isSelected };
        }
        return billOrInvoice;
      }),
    );
  };
  const checkToggleInvoices = () => {
    if (!allCheckbox) {
      setTempBillsOrInvoices(
        tempBillsOrInvoices.map((billOrInvoice, index) => ({
          ...billOrInvoice,
          isSelected: index < 20, // Only check the first 20 items
        })),
      );
    } else {
      setTempBillsOrInvoices(
        tempBillsOrInvoices.map((billOrInvoice) => ({
          ...billOrInvoice,
          isSelected: false,
        })),
      );
    }

    setAllCheckbox(!allCheckbox);
  };
  const uncheckAllInvoices = () => {
    setTempBillsOrInvoices(
      tempBillsOrInvoices.map((billOrInvoice) => ({
        ...billOrInvoice,
        isSelected: false,
      })),
    );
    setAllCheckbox(false);
  };
  const showPartiallyPaid = (invoiceOrBill: vp.TemporaryBillsOrInvoicesRails) => {
    setShowPartiallyPaidDialogue(true);

    setPartiallyPaidPayments(
      (invoiceOrBill.PaymentDetails || []).map((payment) => ({
        date: payment.TransactionDate
          ? moment(payment.TransactionDate).format('MMM DD, YYYY')
          : moment().format('MMM DD, YYYY'),
        amount: `${invoiceOrBill.currency} ${vp.formattedCurrency(
          String(
            Number(vp.sanitizeToNumeric(String(payment.TransactionAmount))) +
              Number(vp.sanitizeToNumeric(String(payment.CreditAmount))),
          ),
        )}`,
        currency: invoiceOrBill.currency,
        transactionId: payment.TransactionID,
        details: [
          {
            label: payment.TransactionID ? (
              payment.PaymentMethod
            ) : (
              <Box gap="sm" base="8" ai="center">
                <Icon icon="accountingSoftware" color="none" height="20px" width="20px" />{' '}
                {`Paid via ${ServiceName} payment method`}
              </Box>
            ),
            amount: `${invoiceOrBill.currency} ${vp.formattedCurrency(String(payment.TransactionAmount))}`,
            visible: payment.TransactionAmount && Number(vp.sanitizeToNumeric(String(payment.TransactionAmount))) > 0,
          },
          {
            label: (
              <Box gap="sm" base="8" ai="center">
                <Icon icon="accountingCredits" color="none" height="20px" width="20px" /> Credits Applied
              </Box>
            ),
            amount: `${invoiceOrBill.currency} ${vp.formattedCurrency(String(payment.CreditAmount))}`,
            visible: payment.CreditAmount && Number(vp.sanitizeToNumeric(String(payment.CreditAmount))) > 0,
          },
        ].filter((detail) => detail.visible),
      })),
    );
  };

  const tableProps: vp.TableProps<vp.TemporaryBillsOrInvoicesRails> = {
    id: 'open-bills-invoices',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'checkbox',
        header: (
          <Checkbox
            name="select-all-invoices"
            value={allCheckbox}
            onBlur={() => {}}
            onChange={checkToggleInvoices}
            size="sm"
            hasNoPadding
          />
        ),
        align: 'left',
        template: ({ data }) => (
          <Checkbox
            name={`select-invoice-${data.id || ''}`}
            value={data.isSelected}
            onBlur={() => {}}
            onChange={() => onCheckedInvoice(data.id)}
            size="sm"
            hasNoPadding
            disabled={tempBillsOrInvoices.filter((i) => i.isSelected).length >= 10 && data.isSelected === false}
          />
        ),
      },
      {
        id: 'date',
        header: Type === 'Receivable' ? t('Table.dateReceivable') : t('Table.date'),
        align: 'left',
        template: ({ data }) => format(new Date(data.postedDate.replace('Z', '')), 'yyyy-MM-dd'),
      },
      {
        id: 'name',
        header: Type === 'Receivable' ? t('Table.nameReceivable') : t('Table.name'),
        align: 'left',
        template: ({ data }) => data.Customer?.Name || data?.customerRef?.name || data?.vendorRef?.name || '',
      },
      {
        id: 'invoiceNumber',
        header: Type === 'Receivable' ? t('Table.invoiceNumberReceivable') : t('Table.invoiceNumber'),
        align: 'left',
        template: ({ data }) => data.invoiceNumber || data.vendorInvoiceNumber || '',
      },
      {
        id: 'amount',
        header: t('Table.amount'),
        align: 'left',
        template: ({ data }) => {
          const hasCredit = data.PaymentDetails?.some(
            (payment) => payment.CreditAmount && Number(vp.sanitizeToNumeric(String(payment.CreditAmount))) > 0,
          );

          return (
            <Box flx="col" gapSize="4px">
              <Box flx="row" gapSize="6px">
                <span>{`${data?.currency || ''} ${vp.formattedCurrency(data.totalAmount.toString())}`}</span>
                {(data.status === 'partiallyPaid' || hasCredit) && (
                  <Button
                    label=""
                    type="button"
                    width="18px"
                    height="18px"
                    background="transparent"
                    color="none"
                    icon="information"
                    iconColor="dark-grey"
                    iconLocation="center"
                    iconWidth="12px"
                    iconHeight="12px"
                    iconSize="xsm"
                    textAlign="center"
                    borderColor="#2d2d2db2"
                    noPadding
                    noShadow
                    isCircle
                    onClick={() => showPartiallyPaid(data)}
                  />
                )}
              </Box>
              {data.status === 'partiallyPaid' && (
                <span className={styles.remainingAmount}>
                  {t('Table.Row.remainingAmount', {
                    currency: data?.currency || '',
                    amount: vp.formattedCurrency(data.amountDue.toString()),
                  })}
                </span>
              )}
            </Box>
          );
        },
      },
      {
        id: 'dueDate',
        header: t('Table.dueDate'),
        align: 'left',
        template: ({ data }) =>
          data.dueDate ? (
            <Box flx="row" ai="center" gapSize="8px">
              <Box flx="col" width="85px">
                <div>{format(new Date(data.dueDate.replace('Z', '')), 'yyyy-MM-dd')}</div>
                <DateDisplay date={new Date(data.dueDate)} />
              </Box>
            </Box>
          ) : (
            ''
          ),
      },
      {
        id: 'paymentMethod',
        header: t('Table.paymentMethod'),
        align: 'left',
        template: ({ data }) =>
          data.hasBankAccount || data.hasCreditCard || data.hasDebitCard || data.hasInterac
            ? t('Table.Row.paymentMethodAvailable')
            : t('Table.Row.paymentMethodNotAvailable'),
      },
      {
        id: 'status',
        header: t('Table.status'),
        align: 'left',
        template: ({ data }) => {
          const isPartiallyPaid = data.status === 'partiallyPaid';
          return (
            <TransactionStatus
              status={isPartiallyPaid ? 'partially paid' : 'open'}
              label={isPartiallyPaid ? t('Table.Row.statusPartiallyPaid') : t('Table.Row.statusOpen')}
            />
          );
        },
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) =>
          data.scheduledDate && data.scheduledDate !== '0000-00-00' ? (
            <Box flx="row" ai="center" jc="fe" gapSize="8px">
              <button
                type="button"
                className={styles.scheduleButton}
                onClick={() => {
                  setUpdatingItem(data);
                  setScheduleDate(moment(data.scheduledDate).valueOf().toString() || String(Date.now()));
                  setShowEditSchedule(true);
                }}
              >
                <Icon icon="calendarAccounting" color="none" width="16px" height="auto" />
                <span>{t('Table.Row.scheduledFor', { date: moment(data.scheduledDate).format('YYYY-MM-DD') })}</span>
              </button>
              <Button
                label=""
                color="transparent"
                icon="cross"
                iconLocation="center"
                iconWidth="10px"
                iconHeight="10px"
                iconColor="error"
                noShadow
                noPadding
                height="10px"
                width="10px"
                id={`scheduled-remove-${data.id || ''}`}
                onClick={() => {
                  setUpdatingItem(data);
                  setShowCancelSchedule(true);
                }}
              />
            </Box>
          ) : (
            <Box jc="fe" gap="sm" ai="center">
              {data.FailureReason && (
                <Tooltip
                  icon="info"
                  position="left"
                  type="hover"
                  color="#d22d2d"
                  bgColor="transparent"
                  width="350px"
                  iconColor="error"
                  borderColor="#d22d2d"
                  information={data.FailureReason}
                />
              )}
              {!data.isSelected && (
                <Button
                  id={`details-${data.id || ''}`}
                  label={
                    data.FailureReason
                      ? t('Table.Row.retryButton')
                      : Type === 'Receivable'
                        ? t('Table.Row.collectButton')
                        : t('Table.Row.payButton')
                  }
                  color="lined"
                  noShadow
                  height="36px"
                  width="100px"
                  onClick={() => {
                    setUpdatingItem(data);
                    setClientName(data.Customer?.Name || data?.customerRef?.name || data?.vendorRef?.name || '');
                    const availableMethods = getAvailablePaymentMethodsDropdown(
                      data,
                      response.Country,
                      Type,
                      () => {},
                    ).filter((payment) => payment.visible && payment.value !== 'new');

                    if (availableMethods.length !== 0) {
                      setShowSingleCollect(true);
                    } else {
                      setUpdateInfo(true);
                    }
                  }}
                />
              )}
            </Box>
          ),
      },
    ],
    rows: tempBillsOrInvoices
      .map((item, i) => ({
        id: item.id || String(i),
        data: item,
        className: item.FailureReason ? 'negative2' : item.isSelected ? 'highlight' : '',
      }))
      .slice(offset, offset + PER_PAGE),
  };

  const onShowScheduleAll = () => {
    setHasItemsWithNoPaymentMethod(
      tempBillsOrInvoices.some(
        (billOrInvoice) =>
          !billOrInvoice.hasBankAccount &&
          !billOrInvoice.hasCreditCard &&
          !billOrInvoice.hasInterac &&
          !billOrInvoice.hasDebitCard,
      ),
    );
    setShowScheduleAll(true);
  };

  useEffect(() => {
    if (onActionsReady) {
      onActionsReady({
        onScheduleAll: onShowScheduleAll,
      });
    }
  }, []);

  useEffect(() => {
    if (showSuccessScheduleDateUpdate) {
      toast.success(t('successScheduleDateUpdateToast'));
    }
  }, [showSuccessScheduleDateUpdate]);

  useEffect(() => {
    if (showSuccessScheduleCanceled) {
      toast.success(t('successScheduleCanceled', { context: Type }));
    }
  }, [showSuccessScheduleCanceled, Type]);

  useEffect(() => {
    if (isReceivableWithPaymentMethodsVisible) {
      toast.success(t('receivableWithPaymentMethodsVisibleToast', { context: Type }));
    }
  }, [isReceivableWithPaymentMethodsVisible, Type]);

  useEffect(() => {
    if (isScheduledForCollectionVisible) {
      toast.success(t('scheduledForCollectionVisibleToast', { context: Type }));
    }
  }, [isScheduledForCollectionVisible, Type]);

  useEffect(() => {
    if (isPaymentMethodSavedVisible) {
      toast.success(t('paymentMethodSavedVisibleToast', { context: Type }));
    }
  }, [isPaymentMethodSavedVisible, Type]);

  useEffect(() => {
    if (isCollectionRequestSentVisible) {
      toast.success(t('collectionRequestSentVisibleToast', { context: Type }));
    }
  }, [isCollectionRequestSentVisible, Type]);

  useEffect(() => {
    if (isCollectionScheduledVisible) {
      toast.success(t('collectionScheduledVisibleToast', { context: Type }));
    }
  }, [isCollectionScheduledVisible, Type]);

  useEffect(() => {
    if (isMultipleRecipientsRequestSentVisible) {
      toast.success(t('multipleRecipientsRequestSentToast', { context: Type }));
    }
  }, [isMultipleRecipientsRequestSentVisible, Type]);

  useEffect(() => {
    if (isMultipleRecipientsScheduledVisible) {
      toast.success(t('multipleRecipientsScheduledToast', { context: Type }));
    }
  }, [isMultipleRecipientsScheduledVisible, Type]);

  useEffect(() => {
    if (isPaymentMethodSavedToRecipientsVisible) {
      toast.success(t('paymentMethodSavedToRecipientsToast', { context: Type }));
    }
  }, [isPaymentMethodSavedToRecipientsVisible, Type]);

  useEffect(() => {
    if (isPartialCollectionVisible) {
      toast.success(t('partialCollectionToast', { context: Type }));
    }
  }, [isPartialCollectionVisible, Type]);

  useEffect(() => {
    if (isFinalCollectionRequestSentVisible) {
      toast.success(t('finalCollectionRequestSentToast', { context: Type }));
    }
  }, [isFinalCollectionRequestSentVisible, Type]);

  return (
    <AccountingSystemContext.Provider value={accountingSystemCtx}>
      <Box flx="col" width="100%" position="relative">
        {selectedBillsOrInvoices.length > 0 && (
          <div className={styles.selectedBar}>
            <Box pt="sm" pb="sm" jc="sb" ai="center">
              <Box gap="none" ai="center">
                <div>{t('itemsSelected', { count: selectedBillsOrInvoices.length })}</div>
              </Box>
              <Box gap="none">
                <Button
                  height="28px"
                  color="secondary"
                  noShadow
                  id="bulk-collect"
                  label={`${Type === 'Receivable' ? t('collectSelectedButton') : t('paySelectedButton')}`}
                  onClick={() => {
                    setConfirmedBillsOrInvoices([...selectedBillsOrInvoices]);
                    setShowBulkPayment(true);
                  }}
                />
                <Icon height="28px" width="25px" icon="line" />
                <Button
                  height="28px"
                  color="yellow"
                  noPadding
                  noShadow
                  id="bulk-cancel"
                  label={tGlobal('Buttons.cancel')}
                  onClick={() => {
                    uncheckAllInvoices();
                  }}
                />
              </Box>
            </Box>
          </div>
        )}
        <Box>
          <Table {...tableProps} />
        </Box>
        <Box width="100%" jc="center" mt="lg" mb="sm">
          {pageCount > 1 && (
            <Pagination
              pageCount={pageCount}
              forcePageNumber={currentPage}
              handlePageClick={({ selected }) => setCurrentPage(selected)}
            />
          )}
        </Box>

        {createPortal(
          <>
            <AccountingSystemSingleDialogue
              isVisible={showSingleCollect}
              close={() => {
                setShowSingleCollect(false);
              }}
              BusinessName={BusinessName}
              ServiceName={ServiceName}
              type={Type}
              billsAndInvoices={updatingItem}
              financialInstitutions={financialInstitutionsResp?.FinancialInstitutions || []}
              onCollect={(collectionAndPaymentItem, isUpdated) => {
                if (isUpdated) {
                  onPayOrCollect([collectionAndPaymentItem], ToastVisibilityState.PaymentMethodSaved);
                } else if (Number(updatingItem?.PaidAmount) !== Number(collectionAndPaymentItem.PaidAmount)) {
                  onPayOrCollect([collectionAndPaymentItem], ToastVisibilityState.PartialCollection);
                } else if (collectionAndPaymentItem.scheduledDate && collectionAndPaymentItem.scheduledDate !== '') {
                  onPayOrCollect([collectionAndPaymentItem], ToastVisibilityState.CollectionScheduled);
                } else {
                  onPayOrCollect([collectionAndPaymentItem], ToastVisibilityState.FinalCollectionRequestSent);
                }
                setShowSingleCollect(false);
              }}
            />

            <AccountingSystemScheduleDialogue
              isVisible={showEditSchedule}
              close={() => {
                setShowEditSchedule(false);
              }}
              defaultDate={scheduleDate}
              onSchedule={(newDate) => {
                if (updatingItem) {
                  updateScheduleDate(newDate, [updatingItem], ToastVisibilityState.ScheduleDateUpdate);
                  setShowEditSchedule(false);
                }
              }}
            />

            <BulkPaymentDialogue
              isVisible={showBulkPayment}
              BusinessName={BusinessName}
              ServiceName={ServiceName}
              close={(selectedList, isSaved) => {
                setShowBulkPayment(false);
                if (isSaved) {
                  setShowCancel(true);
                  setConfirmedBillsOrInvoices(selectedList);
                }
              }}
              onSchedule={(selectedList) => {
                collectOrPayBulkSchedule(selectedList, ToastVisibilityState.MultipleRecipientsScheduled);
                setShowBulkPayment(false);
              }}
              financialInstitutions={financialInstitutionsResp?.FinancialInstitutions || []}
              listOfSelected={confirmedBillsOrInvoices}
              type={Type}
              onScheduleDate={(selectedList, newDate) => {
                setShowScheduleCustomDate(true);
                setConfirmedBillsOrInvoices(selectedList);
                setConfirmedDate(newDate);
                setShowBulkPayment(false);
              }}
              onCollect={(selectedList) => {
                onPayOrCollect(selectedList, ToastVisibilityState.MultipleRecipientsRequestSent);
                setShowBulkPayment(false);
              }}
            />

            <BankingUpdateDialogue
              isVisible={isUpdateInfo}
              BusinessName={BusinessName}
              ServiceName={ServiceName}
              close={() => {
                setUpdateInfo(false);
              }}
              type={Type}
              originalValue={updatingItem}
              financialInstitutions={financialInstitutionsResp?.FinancialInstitutions || []}
              onSave={(selected) => {
                onPayOrCollect([selected], ToastVisibilityState.PaymentMethodSaved);
                setUpdateInfo(false);
              }}
            />

            <AccountingSystemDialogue handleDialogue={handlers.handleAccountDialogue} />
            <PartiallyPaidDialogue
              isVisible={showPartiallyPaidDialogue}
              payments={partiallyPaidPayments}
              close={() => {
                setShowPartiallyPaidDialogue(false);
              }}
            />
          </>,
          document.getElementById('modal-root')!,
        )}
      </Box>
    </AccountingSystemContext.Provider>
  );
  function checkBalance(values: vp.TemporaryBillsOrInvoicesRails[]): boolean {
    const AvailableBalance = Number(AccountBalance.AvailableFunds);
    const totalAmount = values.reduce((accumulator, currentValue) => {
      if (currentValue.scheduledDate && currentValue.scheduledDate !== '') {
        return accumulator;
      }
      const amount = Number(currentValue.PaidAmount);
      return Number(accumulator) + amount;
    }, 0);
    if (AvailableBalance < totalAmount) {
      setShowInsufficientFunds(true);
      return false;
    }
    return true;
  }
  function formatDateDifference(date: Date): string {
    const now = new Date();
    const diffInDays = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays < -1) {
      if (diffInDays < -30) {
        return t('DateHelper.monthsAgo', { days: Math.abs(Math.floor(diffInDays / 30)) });
      }
      if (diffInDays < -7) {
        return t('DateHelper.weeksAgo', { weeks: Math.floor(Math.abs(diffInDays) / 7) });
      }
      return t('DateHelper.daysAgo', { days: Math.abs(diffInDays) });
    }
    if (diffInDays === -1) {
      return t('DateHelper.yesterday');
    }
    if (diffInDays === 0) {
      return t('DateHelper.today');
    }
    if (diffInDays === 1) {
      return t('DateHelper.tomorrow');
    }
    if (diffInDays <= 7) {
      return t('DateHelper.inDays', { days: Math.abs(diffInDays) });
    }
    if (diffInDays < 30) {
      return t('DateHelper.inWeeks', { weeks: Math.floor(diffInDays / 7) });
    }
    return t('DateHelper.inWeeks', { months: Math.floor(diffInDays / 30) });
  }
  function dateDiff(date: Date): number {
    const now = new Date();
    return Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }

  function DateDisplay({ date }: { date: Date }): JSX.Element {
    const formattedDateDifference = formatDateDifference(date);
    const textColor = dateDiff(date) <= 7 ? '#D22D2D' : '#2d2d2db2';
    return (
      <div style={{ color: textColor }} className={styles.dateDiff}>
        {formattedDateDifference}
      </div>
    );
  }
  function formatDateOfObjects(listObject: vp.TemporaryBillsOrInvoicesRails[]): vp.TemporaryBillsOrInvoicesRails[] {
    const today = moment().startOf('day'); // Get today's date, ignoring time
    return listObject.map((item) => {
      const dueDate = moment(item.dueDate);
      // If due date is greater than today's date, set scheduleStartDate as due date
      if (dueDate.isAfter(today, 'day')) {
        return { ...item, scheduledDate: moment(item.dueDate).format('YYYY-MM-DD') };
      }
      return { ...item, scheduledDate: '' }; // Leave blank otherwise
    });
  }
  function enableToastVisibility(state: ToastVisibilityState) {
    switch (state) {
      case ToastVisibilityState.ReceivableWithPaymentMethods:
        setIsReceivableWithPaymentMethodsVisible(true);
        break;
      case ToastVisibilityState.ScheduledForCollection:
        setIsScheduledForCollectionVisible(true);
        break;
      case ToastVisibilityState.PaymentMethodSaved:
        setIsPaymentMethodSavedVisible(true);
        break;
      case ToastVisibilityState.CollectionRequestSent:
        setIsCollectionRequestSentVisible(true);
        break;
      case ToastVisibilityState.CollectionScheduled:
        setIsCollectionScheduledVisible(true);
        break;
      case ToastVisibilityState.MultipleRecipientsRequestSent:
        setIsMultipleRecipientsRequestSentVisible(true);
        break;
      case ToastVisibilityState.MultipleRecipientsScheduled:
        setIsMultipleRecipientsScheduledVisible(true);
        break;
      case ToastVisibilityState.PaymentMethodSavedToRecipients:
        setIsPaymentMethodSavedToRecipientsVisible(true);
        break;
      case ToastVisibilityState.PartialCollection:
        setIsPartialCollectionVisible(true);
        break;
      case ToastVisibilityState.FinalCollectionRequestSent:
        setIsFinalCollectionRequestSentVisible(true);
        break;
      case ToastVisibilityState.ScheduleDateUpdate:
        setShowSuccessScheduleDateUpdated(true);
        break;
      default:
        break;
    }
  }
};
export default AccountingSystemOpen;
